import { allocate, convert, money } from '@fd/money';
import type {
  AccountInfo,
  AllocationSpec,
  ExpenseType,
  IncomePeriod,
  IsoDate,
  Issue,
  IssueCode,
  PersonAmount,
  PersonId,
  ResolvedItem,
  SettlementContext,
  SettlementRule,
  SplitInput,
  SplitMethod,
  TransactionInput,
} from './types.ts';

export const isEffective = (
  version: { effectiveFrom: IsoDate; effectiveTo: IsoDate | null },
  date: IsoDate,
): boolean => version.effectiveFrom <= date && (version.effectiveTo === null || date < version.effectiveTo);

/** Canonical participant order: household sort order, then id. */
export function personOrder(context: SettlementContext): Map<PersonId, number> {
  const sorted = [...context.persons].sort(
    (a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  return new Map(sorted.map((person, index) => [person.id, index]));
}

function sortByPerson<T extends { personId: PersonId }>(items: readonly T[], order: Map<PersonId, number>): T[] {
  const rank = (id: PersonId) => order.get(id) ?? Number.MAX_SAFE_INTEGER;
  return [...items].sort((a, b) => rank(a.personId) - rank(b.personId) || (a.personId < b.personId ? -1 : 1));
}

class ResolutionError extends Error {
  readonly code: IssueCode;
  constructor(code: IssueCode) {
    super(code);
    this.code = code;
  }
}

/** Distributes `total` according to weights per person, in canonical order. */
function distribute(
  total: bigint,
  weights: readonly { personId: PersonId; weight: bigint }[],
  order: Map<PersonId, number>,
  seed: string,
): PersonAmount[] {
  const ordered = sortByPerson(weights, order);
  const parts = allocate(
    total,
    ordered.map((w) => w.weight),
    { tieBreakSeed: seed },
  );
  return ordered.map((w, index) => ({ personId: w.personId, amountMinor: parts[index] as bigint }));
}

function incomeWeight(
  personId: PersonId,
  date: IsoDate,
  context: SettlementContext,
  issues: IssueCode[],
): bigint {
  const period = context.incomes.find(
    (income: IncomePeriod) => income.personId === personId && isEffective(income, date),
  );
  if (!period || period.amountMinor <= 0n) return 0n;
  if (period.currency === context.baseCurrency) return period.amountMinor;
  const rate = context.rateLookup?.(period.currency, context.baseCurrency, date) ?? null;
  if (!rate) {
    issues.push('RATE_MISSING');
    return 0n;
  }
  return convert(money(period.amountMinor, period.currency), rate, context.baseCurrency).minor;
}

/** Splits a positive-or-negative cost among participants according to the allocation spec. */
export function computeOwed(
  cost: bigint,
  spec: AllocationSpec,
  date: IsoDate,
  context: SettlementContext,
  order: Map<PersonId, number>,
  seed: string,
  notes: IssueCode[],
): PersonAmount[] {
  const participants = spec.participants;
  if (participants.length === 0) throw new ResolutionError('NO_PARTICIPANTS');
  const unique = new Set(participants.map((p) => p.personId));
  if (unique.size !== participants.length) throw new ResolutionError('NO_PARTICIPANTS');

  const method: SplitMethod = spec.method;
  switch (method) {
    case 'EQUAL':
      return distribute(cost, participants.map((p) => ({ personId: p.personId, weight: 1n })), order, seed);

    case 'INCOME_PROPORTIONAL': {
      const weights = participants.map((p) => ({
        personId: p.personId,
        weight: incomeWeight(p.personId, date, context, notes),
      }));
      if (weights.every((w) => w.weight === 0n)) {
        notes.push('INCOME_MISSING');
        return distribute(cost, participants.map((p) => ({ personId: p.personId, weight: 1n })), order, seed);
      }
      return distribute(cost, weights, order, seed);
    }

    case 'PERCENTAGES': {
      const weights = participants.map((p) => ({ personId: p.personId, weight: p.value ?? -1n }));
      const total = weights.reduce((acc, w) => acc + w.weight, 0n);
      if (weights.some((w) => w.weight < 0n) || total !== 10_000n) {
        throw new ResolutionError('INVALID_PERCENTAGES');
      }
      return distribute(cost, weights, order, seed);
    }

    case 'SHARES': {
      const weights = participants.map((p) => ({ personId: p.personId, weight: p.value ?? 0n }));
      if (weights.some((w) => w.weight < 0n) || weights.every((w) => w.weight === 0n)) {
        throw new ResolutionError('INVALID_SHARES');
      }
      return distribute(cost, weights, order, seed);
    }

    case 'EXACT': {
      const magnitude = cost < 0n ? -cost : cost;
      const amounts = participants.map((p) => ({ personId: p.personId, amount: p.value ?? -1n }));
      const total = amounts.reduce((acc, a) => acc + a.amount, 0n);
      if (amounts.some((a) => a.amount < 0n) || total !== magnitude) {
        throw new ResolutionError('INVALID_EXACT');
      }
      const sign = cost < 0n ? -1n : 1n;
      return sortByPerson(amounts, order).map((a) => ({ personId: a.personId, amountMinor: a.amount * sign }));
    }
  }
}

function fundingShares(
  account: AccountInfo,
  date: IsoDate,
): { personId: PersonId; weight: bigint }[] | null {
  const version = account.fundingVersions.find((v) => isEffective(v, date));
  if (version) {
    const total = version.shares.reduce((acc, s) => acc + s.basisPoints, 0n);
    if (total !== 10_000n || version.shares.some((s) => s.basisPoints < 0n)) {
      throw new ResolutionError('INVALID_FUNDING');
    }
    return version.shares.map((s) => ({ personId: s.personId, weight: s.basisPoints }));
  }
  if (account.ownerPersonIds.length > 0) {
    return account.ownerPersonIds.map((personId) => ({ personId, weight: 1n }));
  }
  return null;
}

/** Who paid, as weights: explicit payer, else the account's funding rule, else its owners. */
export function resolvePayers(
  transaction: TransactionInput,
  context: SettlementContext,
): { personId: PersonId; weight: bigint }[] {
  if (transaction.paidByPersonId) return [{ personId: transaction.paidByPersonId, weight: 1n }];
  const account = context.accounts.get(transaction.accountId);
  const shares = account ? fundingShares(account, transaction.bookingDate) : null;
  if (!shares) throw new ResolutionError('NO_PAYER');
  return shares;
}

function ruleAllocation(
  rules: readonly SettlementRule[],
  scope: SettlementRule['scope'],
  scopeId: string | null,
  date: IsoDate,
): AllocationSpec | null {
  const rule = rules.find((r) => r.scope === scope && r.scopeId === scopeId);
  return rule?.versions.find((v) => isEffective(v, date))?.allocation ?? null;
}

/**
 * Most specific rule wins: split → transaction → account → category → household
 * (specification §31: household < category < account < transaction < split).
 */
export function resolveAllocation(
  transaction: TransactionInput,
  split: SplitInput | null,
  context: SettlementContext,
  date: IsoDate = transaction.bookingDate,
): AllocationSpec | null {
  const categoryId = split?.categoryId ?? transaction.categoryId;
  return (
    split?.allocation ??
    transaction.allocation ??
    ruleAllocation(context.rules, 'ACCOUNT', transaction.accountId, date) ??
    (categoryId ? ruleAllocation(context.rules, 'CATEGORY', categoryId, date) : null) ??
    ruleAllocation(context.rules, 'HOUSEHOLD', null, date)
  );
}

export function resolveExpenseType(
  transaction: TransactionInput,
  split: SplitInput | null,
  context: SettlementContext,
): ExpenseType {
  const explicit = split?.expenseType ?? transaction.expenseType;
  if (explicit) return explicit;
  const account = context.accounts.get(transaction.accountId);
  if (account?.defaultExpenseType) return account.defaultExpenseType;
  return (account?.ownerPersonIds.length ?? 0) > 1 ? 'SHARED' : 'PERSONAL';
}

type EffectiveSplit = {
  readonly splitId: string | null;
  /** Positive cost for an expense, negative for a refund. */
  readonly cost: bigint;
  readonly split: SplitInput | null;
};

function effectiveSplits(transaction: TransactionInput): EffectiveSplit[] {
  if (transaction.splits.length === 0) {
    return [{ splitId: null, cost: -transaction.amountMinor, split: null }];
  }
  return transaction.splits.map((split) => ({ splitId: split.id, cost: -split.amountMinor, split }));
}

/**
 * Booked and pending costs (D-038: a card payment still pending at the bank counts straight away,
 * as in the owner's own calculator; once booked at another amount the result follows by itself).
 */
export const participatesInSettlement = (transaction: TransactionInput): boolean =>
  (transaction.status === 'BOOKED' || transaction.status === 'PENDING') && (transaction.kind === 'EXPENSE' || transaction.kind === 'REFUND');

/** Owed distribution of one split of an original expense, for reversing it with a refund. */
function resolveOwedForSplit(
  transaction: TransactionInput,
  part: EffectiveSplit,
  context: SettlementContext,
  order: Map<PersonId, number>,
  notes: IssueCode[],
): { expenseType: ExpenseType; method: SplitMethod | null; owed: PersonAmount[] } | null {
  const expenseType = resolveExpenseType(transaction, part.split, context);
  const seed = part.splitId ? `${transaction.id}:${part.splitId}` : transaction.id;
  if (expenseType === 'EXCLUDED') return null;

  if (expenseType === 'PERSONAL') {
    const explicit = part.split?.allocation ?? transaction.allocation;
    if (explicit && explicit.participants.length === 1) {
      const beneficiary = explicit.participants[0]!.personId;
      return { expenseType, method: null, owed: [{ personId: beneficiary, amountMinor: part.cost }] };
    }
    const payers = resolvePayers(transaction, context);
    if (payers.length === 1) {
      return { expenseType, method: null, owed: [{ personId: payers[0]!.personId, amountMinor: part.cost }] };
    }
    throw new ResolutionError('PERSONAL_WITHOUT_BENEFICIARY');
  }

  if (expenseType === 'REIMBURSABLE') {
    const explicit = part.split?.allocation ?? transaction.allocation;
    if (!explicit) throw new ResolutionError('NO_PARTICIPANTS');
    return {
      expenseType,
      method: explicit.method,
      owed: computeOwed(part.cost, explicit, transaction.bookingDate, context, order, seed, notes),
    };
  }

  const spec = resolveAllocation(transaction, part.split, context);
  if (!spec) throw new ResolutionError('NO_PARTICIPANTS');
  return {
    expenseType,
    method: spec.method,
    owed: computeOwed(part.cost, spec, transaction.bookingDate, context, order, seed, notes),
  };
}

/**
 * Resolves one transaction into settlement items. Issues are reported instead of guessing:
 * an item that cannot be resolved safely is left out and flagged for review.
 */
export function resolveTransaction(
  transaction: TransactionInput,
  context: SettlementContext,
  order: Map<PersonId, number> = personOrder(context),
): { items: ResolvedItem[]; issues: Issue[] } {
  const items: ResolvedItem[] = [];
  const issues: Issue[] = [];
  if (!participatesInSettlement(transaction)) return { items, issues };

  const report = (splitId: string | null, code: IssueCode) =>
    issues.push({ transactionId: transaction.id, splitId, code });

  let payers: { personId: PersonId; weight: bigint }[];
  try {
    payers = resolvePayers(transaction, context);
  } catch (error) {
    report(null, error instanceof ResolutionError ? error.code : 'NO_PAYER');
    return { items, issues };
  }

  const refundOf = transaction.kind === 'REFUND' ? transaction.refundOfTransactionId ?? null : null;
  if (refundOf) {
    const original = context.transactionsById?.get(refundOf);
    if (!original) {
      report(null, 'REFUND_ORIGINAL_MISSING');
      return { items, issues };
    }
    const originalParts = effectiveSplits(original);
    const weights = originalParts.map((p) => (p.cost < 0n ? -p.cost : p.cost));
    const refundCost = -transaction.amountMinor; // negative
    const portions = weights.some((w) => w > 0n)
      ? allocate(refundCost, weights, { tieBreakSeed: original.id })
      : [];
    originalParts.forEach((part, index) => {
      const notes: IssueCode[] = [];
      try {
        const resolved = resolveOwedForSplit(original, part, context, order, notes);
        if (!resolved) return;
        const portion = portions[index] ?? 0n;
        const seed = part.splitId ? `${original.id}:${part.splitId}` : original.id;
        const owed = distribute(
          portion,
          resolved.owed.map((o) => ({ personId: o.personId, weight: o.amountMinor < 0n ? -o.amountMinor : o.amountMinor })),
          order,
          seed,
        );
        const paid = distribute(portion, payers, order, `${transaction.id}:paid`);
        items.push({
          transactionId: transaction.id,
          splitId: part.splitId,
          bookingDate: transaction.bookingDate,
          currency: transaction.currency,
          expenseType: resolved.expenseType,
          method: resolved.method,
          costMinor: portion,
          paid,
          owed,
        });
        notes.forEach((code) => report(part.splitId, code));
      } catch (error) {
        report(part.splitId, error instanceof ResolutionError ? error.code : 'NO_PARTICIPANTS');
      }
    });
    return { items, issues };
  }

  for (const part of effectiveSplits(transaction)) {
    const notes: IssueCode[] = [];
    try {
      const resolved = resolveOwedForSplit(transaction, part, context, order, notes);
      if (!resolved) continue;
      const seed = part.splitId ? `${transaction.id}:${part.splitId}` : transaction.id;
      items.push({
        transactionId: transaction.id,
        splitId: part.splitId,
        bookingDate: transaction.bookingDate,
        currency: transaction.currency,
        expenseType: resolved.expenseType,
        method: resolved.method,
        costMinor: part.cost,
        paid: distribute(part.cost, payers, order, `${seed}:paid`),
        owed: resolved.owed,
      });
      notes.forEach((code) => report(part.splitId, code));
    } catch (error) {
      report(part.splitId, error instanceof ResolutionError ? error.code : 'NO_PARTICIPANTS');
    }
  }
  return { items, issues };
}
